import { toast } from "sonner";
import { Loader } from "@/shared/ui/Loader";
import { FormRoot, FormInput, FormPassword, FormCheckbox, RHF, zodResolver } from "@/shared/forms";
import { Button } from "@/components/ui/button";

import { useNavigate } from "@tanstack/react-router";
import z from "zod";
import { authClient } from "../api/client";
import { useAuthState } from "../model/useAuthState";

const SignInFormSchema = z.object({
  email: z.string().email({ message: "ອີເມວບໍ່ຖືກຕ້ອງ" }),
  password: z.string().min(1, { message: "ກະລຸນາໃສ່ລະຫັດຜ່ານ" }),
  rememberMe: z.boolean().optional(),
});

type ISignInFormSchema = z.infer<typeof SignInFormSchema>;

export default function SignInForm() {
  const navigate = useNavigate({ from: "/" });
  const { isLoading } = useAuthState();

  const form = RHF.useForm({
    defaultValues: { email: "", password: "" },
    resolver: zodResolver(SignInFormSchema),
  });

  const handleSubmit = async (value: ISignInFormSchema) => {
    await authClient.signIn.email(
      { email: value.email, password: value.password },
      {
        onSuccess: (ctx) => {
          if (
            ctx.data &&
            typeof ctx.data === "object" &&
            "twoFactorRedirect" in ctx.data &&
            ctx.data.twoFactorRedirect
          ) {
            void navigate({ to: "/auth/two-factor" });
            return;
          }
          void navigate({ to: "/app/dashboard" });
          toast.success("ເຂົ້າລະບົບສໍາເລັດ");
        },
        onError: (error) => {
          toast.error(error.error.message || error.error.statusText);
        },
      },
    );
  };

  if (isLoading) {
    return <Loader />;
  }

  return (
    <FormRoot<ISignInFormSchema> methods={form} onSubmit={handleSubmit}>
      <FormInput
        name="email"
        label="ອີເມວ"
        requiredMark
        placeholder="name@example.com"
      />
      <FormPassword
        name="password"
        label="ລະຫັດຜ່ານ"
        requiredMark
        placeholder="********"
      />

      <FormCheckbox name="rememberMe" label="ຈໍາຂ້ອຍໄວ້" />

      <Button
        type="submit"
        isLoading={isLoading}
        className="w-full rounded-full"
      >
        ເຂົ້າລະບົບ
      </Button>
    </FormRoot>
  );
}
